import {gql} from '@apollo/client';
import {Alert, Button, Checkbox, Group, Modal, Stack, TextInput} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useEffect, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import * as TagSchemaActions from 'tag-schema/TagSchemaActions';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import LegacyButton from 'ui-library/buttons/Button';

import {
  DuplicateTagSchemaModalQuery,
  DuplicateTagSchemaModalQueryVariables,
} from './__generated/DuplicateTagSchemaModalQuery';

const DUPLICATE_TAG_SCHEMA_MODAL_QUERY = gql`
  query DuplicateTagSchemaModalQuery($tagSchemaId: StrictString!) @skipAudit @engComponent(name: SCHEMAS) {
    tagSchema(id: $tagSchemaId) {
      ... on TagSchemaNode {
        id
        prefix
        prefixSource {
          id
          name
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface DuplicateTagSchemaModalProps {
  tagSchemaId: string;
  tagSchemaName: string;
  opened: boolean;
  onClose: () => void;
}

function DuplicateTagSchemaModal(props: DuplicateTagSchemaModalProps): JSX.Element {
  const [name, setName] = useState(`${props.tagSchemaName} (Duplicate)`);
  const [prefix, setPrefix] = useState('');
  const [sqlIdentifier, setSqlIdentifier] = useState('');
  const [copyCollaborators, setCopyCollaborators] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryResult = useSkippableBenchlingQuery<
    DuplicateTagSchemaModalQuery,
    DuplicateTagSchemaModalQueryVariables
  >(DUPLICATE_TAG_SCHEMA_MODAL_QUERY, {
    variables: {tagSchemaId: props.tagSchemaId},
    skip: !props.opened,
  });

  const tagSchemaData = queryResult.type === QueryResultType.SUCCESS ? queryResult.data.tagSchema : null;
  const tagSchemaInaccessible = tagSchemaData != null && isInaccessible(tagSchemaData);
  const tagSchema = tagSchemaData != null && !tagSchemaInaccessible ? tagSchemaData : null;
  const prefixSource = tagSchema?.prefixSource ?? null;
  const sourcePrefix = tagSchema?.prefix ?? '';
  const hasPrefixSource = prefixSource != null;

  useEffect(() => {
    if (props.opened) {
      setName(`${props.tagSchemaName} (Duplicate)`);
      setPrefix('');
      setSqlIdentifier('');
      setCopyCollaborators(true);
      setIsSubmitting(false);
    }
  }, [props.opened, props.tagSchemaName]);

  useEffect(() => {
    if (props.opened && hasPrefixSource && sourcePrefix) {
      setPrefix(sourcePrefix);
    }
  }, [props.opened, hasPrefixSource, sourcePrefix]);

  const queryLoaded = queryResult.type === QueryResultType.SUCCESS && !tagSchemaInaccessible;
  const canSubmit = name.trim().length > 0 && prefix.trim().length > 0 && !isSubmitting && queryLoaded;

  const handleSubmit = async (): Promise<void> => {
    if (!canSubmit) {
      return;
    }
    setIsSubmitting(true);
    try {
      const analyticsMetadata = {
        actionOrigin: ActionOrigin.SETTINGS_ENTITY_SCHEMA_PAGE,
        actionMethod: 'DUPLICATE_TAG_SCHEMA_BUTTON',
      };
      const result = await TagSchemaActions.duplicateTagSchema(
        props.tagSchemaId,
        {
          name: name.trim(),
          prefix: prefix.trim(),
          sqlIdentifier: sqlIdentifier.trim() || undefined,
          copyCollaborators,
        },
        analyticsMetadata
      );
      if (result != null) {
        props.onClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal opened={props.opened} onClose={props.onClose} title="Duplicate entity schema">
      <Stack spacing="sm">
        <Alert>
          Some settings won't carry over to the duplicate entity schema and will need to be reconfigured.
          Examples include computed fields, validation rules, and name templates (unless set by a fieldset).
        </Alert>
        {queryResult.type === QueryResultType.ERROR && (
          <Alert color="red">Failed to load schema details. Please try again.</Alert>
        )}
        {tagSchemaInaccessible && (
          <Alert color="red">You do not have permission to access this schema.</Alert>
        )}
        <TextInput
          label="Name"
          required={true}
          value={name}
          onChange={(e) => {
            setName(e.currentTarget.value);
          }}
        />
        <TextInput
          label="Prefix"
          required={true}
          value={prefix}
          disabled={hasPrefixSource}
          description={hasPrefixSource ? `Set by fieldset "${prefixSource?.name}"` : undefined}
          onChange={(e) => {
            setPrefix(e.currentTarget.value);
          }}
        />
        {BenchlingConfig.SCHEMAS_ENABLE_VIEW_SYSTEM_NAME &&
          BenchlingConfig.SCHEMAS_ENABLE_CHANGE_WAREHOUSE_NAME && (
            <TextInput
              label="System name"
              value={sqlIdentifier}
              onChange={(e) => {
                setSqlIdentifier(e.currentTarget.value);
              }}
            />
          )}
        <Checkbox
          label="Copy access policies"
          checked={copyCollaborators}
          onChange={(e) => {
            setCopyCollaborators(e.currentTarget.checked);
          }}
        />
        <Group position="right" mt="md">
          <Button variant="tertiary" onClick={props.onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit} loading={isSubmitting}>
            Duplicate
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

interface DuplicateTagSchemaButtonProps {
  tagSchemaId: string;
  tagSchemaName: string;
}

export default function DuplicateTagSchemaButton(props: DuplicateTagSchemaButtonProps): JSX.Element | null {
  const [opened, {open, close}] = useDisclosure(false);

  if (!BenchlingConfig.SCHEMAS_ENABLE_DUPLICATE_TAG_SCHEMA) {
    return null;
  }

  return (
    <>
      <LegacyButton
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        onClick={open}
        size={ButtonSize.SMALL}
        text="Duplicate"
      />
      <DuplicateTagSchemaModal
        tagSchemaId={props.tagSchemaId}
        tagSchemaName={props.tagSchemaName}
        opened={opened}
        onClose={close}
      />
    </>
  );
}
